import React, { useState } from 'react';
import { Users, Mail, CheckCircle, MapPin, Globe, MessageSquare, ExternalLink, Calendar, Network } from 'lucide-react';
import { saveEmailOnly, updateWithQuestionnaire, submitFeedbackOnly } from './firebase/betaSignups';

const BetaSignup = () => {
  const [step, setStep] = useState(1); // 1 = email, 2 = questionnaire, 3 = success
  const [loading, setLoading] = useState(false);
  const [signupId, setSignupId] = useState(null); // Track the signup document ID
  const [hasEmail, setHasEmail] = useState(false); // Track if user provided email
  
  const [formData, setFormData] = useState({
    email: '',
    primaryGoals: [],
    travelWillingness: [],
    languages: [],
    preferredConcepts: [],
    comments: ''
  });

  const primaryGoalOptions = [
    { id: 'events', label: 'Find local events and activities', icon: MapPin }, // simpler
    { id: 'meetPeople', label: 'Meet people who share my interests', icon: Users }, // more natural
    { id: 'explore', label: 'Discover new places and try new things', icon: Globe }, // more exciting
    { id: 'networking', label: 'Connect professionally', icon: MessageSquare } // shorter
  ];

  const travelOptions = [
    { id: 'cityOnly', label: 'Just my city' },
    { id: 'region', label: 'My region (e.g., German-speaking Switzerland)' },
    { id: 'switzerland', label: 'Anywhere in Switzerland' },
    { id: 'flexible', label: 'Depends on the event!' }
  ];

  const languageOptions = [
    { id: 'english', label: 'English' },
    { id: 'german', label: 'German' },
    { id: 'french', label: 'French' },
    { id: 'italian', label: 'Italian' }
    //{ id: 'swiss-german', label: 'Swiss German' }
  ];

  const conceptOptions = [
    {
      id: 'directory',
      label: 'Community Directory',
      description: 'Join communities, access group chats, and discover local events',
      icon: Network,
      demoUrl: '/concept-directory',
      image: '/images/concept-directory.png',
      color: 'bg-purple-50 border-purple-200 hover:border-purple-400'
    },
    {
      id: 'matching',
      label: 'User Matching',
      description: 'Browse profiles and connect with people who share your interests',
      icon: Users,
      demoUrl: '/concept-matching',
      image: '/images/concept-matching.png',
      color: 'bg-blue-50 border-blue-200 hover:border-blue-400'
    },
    {
      id: 'events',
      label: 'Events Platform',
      description: 'Discover and join events happening near you across Switzerland',
      icon: Calendar,
      demoUrl: '/concept-events',
      image: '/images/concept-events.png',
      color: 'bg-green-50 border-green-200 hover:border-green-400'
    }
  ];

  const toggleSelection = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(v => v !== value)
        : [...prev[field], value]
    }));
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (formData.email) {
      setLoading(true);
      
      console.log('Submitting email:', formData.email);
      
      // Save email immediately to database
      const result = await saveEmailOnly(formData.email);
      
      console.log('Save result:', result);
      
      if (result.success) {
        console.log('Email saved successfully! Signup ID:', result.signupId);
        setSignupId(result.signupId); // Store the document ID
        setHasEmail(true); // Mark that user provided email
        setStep(2); // Move to questionnaire
      } else {
        console.error('Error saving email:', result.error);
        alert('Error saving email: ' + result.error);
      }
      
      setLoading(false);
    }
  };

  const handleContinueWithoutEmail = () => {
    setHasEmail(false); // Mark that user did NOT provide email
    setStep(2); // Move to questionnaire
  };

  const handleQuestionnaireSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (hasEmail && signupId) {
      // Update the existing signup with questionnaire data
      const questionnaireData = {
        primaryGoals: formData.primaryGoals,
        travelWillingness: formData.travelWillingness,
        languages: formData.languages,
        preferredConcepts: formData.preferredConcepts,
        comments: formData.comments
      };

      const result = await updateWithQuestionnaire(signupId, questionnaireData);
      
      if (result.success) {
        setStep(3);
      } else {
        alert('Error submitting questionnaire. Please try again.');
      }
    } else {
      // Feedback only - save to database as feedback-only submission
      const questionnaireData = {
        primaryGoals: formData.primaryGoals,
        travelWillingness: formData.travelWillingness,
        languages: formData.languages,
        preferredConcepts: formData.preferredConcepts,
        comments: formData.comments
      };

      const result = await submitFeedbackOnly(questionnaireData);
      
      if (result.success) {
        setStep(3);
      } else {
        alert('Error submitting feedback. Please try again.');
      }
    }
    
    setLoading(false);
  };

  // Step 1: Email Collection
  if (step === 1) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-50 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full">
          {/* Hero Section */}
          <div className="text-center mb-6">
            <div className="flex justify-center mb-4">
              <div className="bg-red-100 p-3 rounded-full">
                <Users className="w-10 h-10 text-red-600" />
              </div>
            </div>
            <h1 className="text-4xl font-bold mb-3">
              Join the <span className="text-gray-900">Swiss</span><span className="text-red-600">Meet</span> Beta
            </h1>
            <p className="text-base text-gray-600 max-w-xl mx-auto">
              We're building a platform to connect people and discover events across Switzerland.
              Join our beta and help us make what you need.
            </p>
          </div>

          {/* Email Form */}
          <div className="bg-white rounded-3xl shadow-2xl p-6 md:p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              Get Early Access
            </h2>
            {/*<p className="text-gray-600 text-center mb-6">
              Join the waitlist and help shape SwissMeet.
            </p>
            */  }

            <form onSubmit={handleEmailSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-4 top-4 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all text-lg"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 bg-gradient-to-r from-red-500 to-red-600 text-white font-semibold rounded-xl hover:from-red-600 hover:to-red-700 transform hover:scale-[1.02] transition-all duration-300 shadow-lg text-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Saving...' : 'Continue →'}
              </button>

              <div className="text-center">
                <span className="text-gray-400 text-sm">or</span>
              </div>

              <button
                type="button"
                onClick={handleContinueWithoutEmail}
                className="w-full py-4 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition-all duration-300 text-lg border-2 border-gray-300 hover:border-gray-400"
              >
                Continue without email (feedback only)
              </button>

              <p className="text-xs text-gray-500 text-center">
                We'll only email you when we launch. No spam, ever. Privacy-first.
              </p>
            </form>
          </div>

          {/* Social Proof */}
          {/*</div><div className="text-center mt-8 text-gray-600">*/}
          <div className="text-center text-xs mt-6 text-gray-500">
            <p className="text-xs">
              Made with ❤️ by EPFL alumni, for the Swiss community. 
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Step 2: Questionnaire
  if (step === 2) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-50 p-4 py-12">
        <div className="max-w-3xl mx-auto">
          {/* Progress */}
          <div className="mb-8">
            <div className="flex items-center justify-center space-x-2 text-sm text-gray-600 mb-2">
              <span>Step 2 of 2</span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full bg-red-600 w-2/2 transition-all duration-500"></div>
            </div>
          </div>

          <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-2 text-center">
              Help us understand you better
            </h2>
            <p className="text-gray-600 mb-8 text-center">
              This will help us build exactly what you need
            </p>

            <form onSubmit={handleQuestionnaireSubmit} className="space-y-8">
              {/* Primary Goals */}
              <div>
                <label className="block text-lg font-semibold text-gray-900 mb-4">
                  What's most important to you on SwissMeet? <span className="text-red-600">*</span>
                </label>
                <div className="space-y-3">
                  {primaryGoalOptions.map(option => {
                    const Icon = option.icon;
                    const isSelected = formData.primaryGoals.includes(option.id);
                    return (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => toggleSelection('primaryGoals', option.id)}
                        className={`w-full flex items-center space-x-4 p-4 rounded-xl border-2 transition-all text-left ${
                          isSelected
                            ? 'border-red-500 bg-red-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className={`flex-shrink-0 ${isSelected ? 'text-red-600' : 'text-gray-400'}`}>
                          <Icon className="w-6 h-6" />
                        </div>
                        <span className={`flex-1 ${isSelected ? 'text-gray-900 font-medium' : 'text-gray-700'}`}>
                          {option.label}
                        </span>
                        {isSelected && (
                          <CheckCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Travel Willingness */}
              <div>
                <label className="block text-lg font-semibold text-gray-900 mb-4">
                  How far would you travel for an event or meetup? <span className="text-red-600">*</span>
                </label>
                <div className="space-y-3">
                  {travelOptions.map(option => {
                    const isSelected = formData.travelWillingness.includes(option.id);
                    return (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => toggleSelection('travelWillingness', option.id)}
                        className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all text-left ${
                          isSelected
                            ? 'border-red-500 bg-red-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <span className={isSelected ? 'text-gray-900 font-medium' : 'text-gray-700'}>
                          {option.label}
                        </span>
                        {isSelected && (
                          <CheckCircle className="w-6 h-6 text-red-600" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Languages */}
              <div>
                <label className="block text-lg font-semibold text-gray-900 mb-4">
                  My language(s): <span className="text-red-600">*</span>
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {languageOptions.map(option => {
                    const isSelected = formData.languages.includes(option.id);
                    return (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => toggleSelection('languages', option.id)}
                        className={`flex items-center justify-between p-4 rounded-xl border-2 transition-all text-left ${
                          isSelected
                            ? 'border-red-500 bg-red-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <span className={isSelected ? 'text-gray-900 font-medium' : 'text-gray-700'}>
                          {option.label}
                        </span>
                        {isSelected && (
                          <CheckCircle className="w-5 h-5 text-red-600" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Concept Preferences */}
              <div className="pt-6 border-t-2 border-gray-100">
                <label className="block text-lg font-semibold text-gray-900 mb-2">
                  Which approach interests you most? <span className="text-red-600">*</span>
                </label>
                <p className="text-sm text-gray-600 mb-4">
                  Click "View Demo" to explore each concept, then select your favorite(s)
                </p>
                <div className="space-y-6">
                  {conceptOptions.map(concept => {
                    const Icon = concept.icon;
                    const isSelected = formData.preferredConcepts.includes(concept.id);
                    return (
                      <div
                        key={concept.id}
                        className={`rounded-xl border-2 transition-all overflow-hidden ${
                          isSelected
                            ? 'border-red-500 bg-red-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        {/* Screenshot Thumbnail */}
                        <div className="relative">
                          <img 
                            src={concept.image} 
                            alt={`${concept.label} preview`}
                            className="w-full h-48 object-cover object-top"
                          />
                          {isSelected && (
                            <div className="absolute top-3 right-3 bg-red-600 text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center">
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Selected
                            </div>
                          )}
                        </div>

                        {/* Content */}
                        <div className="p-5">
                          <div className="flex items-start space-x-3 mb-3">
                            <div className={`flex-shrink-0 mt-1 ${isSelected ? 'text-red-600' : 'text-gray-600'}`}>
                              <Icon className="w-6 h-6" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-bold text-gray-900 text-lg mb-1">{concept.label}</h4>
                              <p className="text-sm text-gray-600">{concept.description}</p>
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="flex items-center space-x-3">
                            <a
                              href={concept.demoUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex-1 inline-flex items-center justify-center px-5 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all text-sm font-semibold shadow-md hover:shadow-lg"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <ExternalLink className="w-4 h-4 mr-2" />
                              Click for DEMO
                            </a>
                            <button
                              type="button"
                              onClick={() => toggleSelection('preferredConcepts', concept.id)}
                              className={`flex-1 inline-flex items-center justify-center px-5 py-3 rounded-lg transition-all text-sm font-semibold ${
                                isSelected
                                  ? 'bg-green-600 text-white hover:bg-green-700 shadow-md'
                                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-2 border-gray-300'
                              }`}
                            >
                              {isSelected ? (
                                <>
                                  <CheckCircle className="w-4 h-4 mr-2" />
                                  I Like This
                                </>
                              ) : (
                                'I Like This'
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Comments/Suggestions */}
              <div className="pt-6 border-t-2 border-gray-100">
                <label className="block text-lg font-semibold text-gray-900 mb-2">
                  Any comments or suggestions?
                </label>
                <p className="text-sm text-gray-600 mb-4">
                  Tell us what you'd like to see, any concerns, or additional feedback
                </p>
                <textarea
                  value={formData.comments}
                  onChange={(e) => setFormData(prev => ({ ...prev, comments: e.target.value }))}
                  placeholder="Share your thoughts here... (optional)"
                  rows={4}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all resize-none"
                />
              </div>

              {/* Submit */}
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={loading || formData.primaryGoals.length === 0 || formData.travelWillingness.length === 0 || formData.languages.length === 0 || formData.preferredConcepts.length === 0}
                  className="w-full py-3 bg-gradient-to-r from-red-500 to-red-600 text-white font-semibold rounded-xl hover:from-red-600 hover:to-red-700 transform hover:scale-[1.02] transition-all duration-300 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {loading ? 'Submitting...' : 'Submit'}
                </button>
                
                {/* 
                <button
                  type="button"
                  onClick={() => setStep(3)}
                  className="w-full mt-3 py-3 text-gray-600 hover:text-gray-900 transition-colors font-medium"
                >
                  Skip for now 
                </button>
                */}
                
                <p className="text-xs text-gray-500 text-center mt-2">
                  The questionnaire helps us build what you need. Thank you for your time! 
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Step 3: Success
  if (hasEmail) {
    // Step 3a: Success with email (beta signup)
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full text-center">
          <div className="bg-white rounded-3xl shadow-2xl p-12">
            <div className="flex justify-center mb-6">
              <div className="bg-green-100 p-6 rounded-full">
                <CheckCircle className="w-16 h-16 text-green-600" />
              </div>
            </div>
            
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              You're on the list! 🎉
            </h1>
            
            <p className="text-xl text-gray-600 mb-8">
              We'll send updates to <strong>{formData.email}</strong>
            </p>

            <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6 mb-8">
              <p className="text-gray-700 mb-4">
                <strong>What's next?</strong>
              </p>
              <ul className="text-left space-y-2 text-gray-600">
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">•</span>
                  We'll review your responses and build features you want
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">•</span>
                  You'll get early access when we launch (coming soon!)
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">•</span>
                  If you have more feedback or want to reach out, contact me by email (swissmeetwebsite@gmail.com) or on reddit (u/swissmeetwebsite/)
                </li>
              </ul>
            </div>

            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Thanks so much! See you soon.
            </h2>
          </div>
        </div>
      </div>
    );
  } else {
    // Step 3b: Success without email (feedback only)
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full text-center">
          <div className="bg-white rounded-3xl shadow-2xl p-12">
            <div className="flex justify-center mb-6">
              <div className="bg-green-100 p-6 rounded-full">
                <CheckCircle className="w-16 h-16 text-green-600" />
              </div>
            </div>
            
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Thank you for your feedback! 💙
            </h1>
            
            <p className="text-xl text-gray-600 mb-8">
              Your input helps us build exactly what the community needs.
            </p>

            <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6 mb-8">
              <p className="text-gray-700 mb-4">
                <strong>Have more feedback or questions?</strong>
              </p>
              <p className="text-gray-600">
                Contact us by email at <strong>swissmeetwebsite@gmail.com</strong> or on reddit at <strong>u/swissmeetwebsite</strong>
              </p>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Thanks so much! 
            </h2>
          </div>
        </div>
      </div>
    );
  }
};

export default BetaSignup;
